/** Pure filter/layout helpers for wheel put/call screener (browser + Node tests). */

export const KNOWN_DECISIONS = new Set([
  "Good", "Selective", "Small size", "Avoid", "Insufficient data",
]);

export const DECISION_DISPLAY = {
  Good: "High",
  Selective: "Moderate",
  "Small size": "Lower",
  Avoid: "Weak/flagged",
  "Insufficient data": "Insufficient data",
};

export const RUBRIC_SORT_RANK = {
  Good: 1,
  Selective: 2,
  "Small size": 3,
  Avoid: 4,
  "Insufficient data": 5,
  __unavailable__: 90,
  __other__: 91,
  __unknown__: 92,
  __none__: 93,
};

export function displayDecision(key) {
  if (!key) return "";
  return DECISION_DISPLAY[key] || key;
}

export function verdictTier(key) {
  const m = {
    Good: "good", Selective: "selective", "Small size": "small",
    Avoid: "avoid", "Insufficient data": "insufficient",
  };
  return m[key] || "insufficient";
}

export function rubricFilterKey(row) {
  if (row.report_state === "missing") return "__none__";
  if (row.report_state !== "available") return "__unknown__";
  if (!row.decision) return "__unavailable__";
  return KNOWN_DECISIONS.has(row.decision) ? row.decision : "__other__";
}

export function compareRubricRows(a, b, dir) {
  const ka = RUBRIC_SORT_RANK[rubricFilterKey(a)] ?? 50;
  const kb = RUBRIC_SORT_RANK[rubricFilterKey(b)] ?? 50;
  const unavailable = 90;
  const aBad = ka >= unavailable;
  const bBad = kb >= unavailable;
  if (aBad !== bBad) return aBad ? 1 : -1;
  return (ka - kb) * dir;
}

/** Index-membership facet codes for put/call screener chips (`none` = no badge). */
export const INDEX_FILTER_CODES = ["none", "sp500", "ndx", "dji", "rut"];

/** Finviz-style market-cap tier keys (millions thresholds). */
export const CAP_FILTER_CODES = ["mega", "large", "mid", "small", "micro", "nano", "unknown"];

/** Default cap filter — all tiers selected (no-op). */
export const DEFAULT_CAP_FILTER = CAP_FILTER_CODES.slice();

/** Default screener index filter — S&P 500 only. */
export const DEFAULT_INDEX_FILTER = ["sp500"];

export function migrateFilterStateV1toV2(saved, side) {
  const base = saved && typeof saved === "object" ? { ...saved } : {};
  if (!base.earnings) base.earnings = "before";
  if (!Array.isArray(base.rubricBands)) base.rubricBands = [];
  if (!Array.isArray(base.sectors)) base.sectors = [];
  if (typeof base.ticker !== "string") base.ticker = "";
  if (!Array.isArray(base.caps) || !base.caps.length) base.caps = DEFAULT_CAP_FILTER.slice();
  if (side === "put" && !Array.isArray(base.indexes)) base.indexes = DEFAULT_INDEX_FILTER.slice();
  if (side === "put" && base.maxCollateral == null) base.maxCollateral = 0;
  return base;
}

export function sanitizeCapFilter(selected) {
  const sel = Array.isArray(selected) ? selected.filter((k) => CAP_FILTER_CODES.includes(k)) : [];
  if (!sel.length) return DEFAULT_CAP_FILTER.slice();
  return sel;
}

export function classifyMarketCapTier(marketCap) {
  const m = Number(marketCap);
  if (!Number.isFinite(m) || m <= 0) return "unknown";
  if (m >= 200_000) return "mega";
  if (m >= 10_000) return "large";
  if (m >= 2_000) return "mid";
  if (m >= 300) return "small";
  if (m >= 50) return "micro";
  return "nano";
}

export function fmtMktCapBillions(millions) {
  const m = Number(millions);
  if (!Number.isFinite(m) || m <= 0) return "—";
  const billions = m / 1000;
  const digits = billions >= 10 ? 1 : 2;
  return "$" + billions.toFixed(digits) + "B";
}

export function rowMatchesCap(row, selected) {
  const sel = sanitizeCapFilter(selected);
  if (!sel.length || sel.length >= CAP_FILTER_CODES.length) return true;
  return sel.indexOf(classifyMarketCapTier(row && row.market_cap)) >= 0;
}

export function compareNullableNum(a, b, dir, key) {
  const av = a && a[key];
  const bv = b && b[key];
  const an = Number(av);
  const bn = Number(bv);
  const aNull = av == null || Number.isNaN(an);
  const bNull = bv == null || Number.isNaN(bn);
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (an < bn) return -1 * dir;
  if (an > bn) return 1 * dir;
  return 0;
}

function dteInBucket(dte, bucket) {
  const n = Number(dte);
  if (Number.isNaN(n)) return false;
  return n >= bucket.min && n <= bucket.max;
}

function normalizeDteSelection(selected, buckets) {
  if (Array.isArray(selected)) {
    const keys = new Set((buckets || []).map((b) => b.key));
    const sel = selected.filter((k) => keys.has(k));
    if (!sel.length || sel.length >= keys.size) return null;
    return sel;
  }
  if (selected && selected !== "all") return [selected];
  return null;
}

export function rowMatchesDte(row, selected, buckets) {
  const sel = normalizeDteSelection(selected, buckets);
  if (!sel) return true;
  return sel.some((key) => {
    const b = buckets.find((x) => x.key === key);
    return b && dteInBucket(row.dte, b);
  });
}

export function matchesBaseFilters(row, state, side, buckets, isTradableQuote) {
  if (state.quote === "tradable" && !isTradableQuote(row)) return false;
  if (!rowMatchesDte(row, state.dte, buckets)) return false;
  if (side === "put" && Number(state.maxCollateral) > 0) {
    if ((Number(row.collateral) || 0) > Number(state.maxCollateral)) return false;
  }
  if (state.earnings === "before" && row.expires_after_earnings) return false;
  return true;
}

export function rowMatchesSector(row, selected) {
  if (!selected || !selected.length) return true;
  const s = (row.sector == null || row.sector === "") ? "Unknown" : String(row.sector).trim();
  return selected.indexOf(s) >= 0;
}

export function rowMatchesRubric(row, selected) {
  if (!selected || !selected.length) return true;
  return selected.indexOf(rubricFilterKey(row)) >= 0;
}

export function rowMatchesIndex(row, selected) {
  const sel = selected || [];
  if (!sel.length || sel.length >= INDEX_FILTER_CODES.length) return true;
  const badges = row.index_badges || [];
  const badgeSet = new Set(badges);
  const noIndex = badges.length === 0;
  for (const code of sel) {
    if (code === "none" && noIndex) return true;
    if (code !== "none" && badgeSet.has(code)) return true;
  }
  return false;
}

/** History-style ticker prefix (case-insensitive). Empty query matches all. */
export function rowMatchesTickerPrefix(row, query, tickerKeys) {
  const q = String(query || "").trim().toUpperCase();
  if (!q) return true;
  const keys = tickerKeys && tickerKeys.length
    ? tickerKeys
    : ["underlying_ticker", "ticker"];
  for (const k of keys) {
    const t = row && row[k];
    if (t != null && String(t).toUpperCase().startsWith(q)) return true;
  }
  return false;
}

export function filterRecommendationRows(rawRows, state, side, buckets, isTradableQuote) {
  return (rawRows || []).filter((r) => {
    if (!matchesBaseFilters(r, state, side, buckets, isTradableQuote)) return false;
    if (side === "put" && !rowMatchesIndex(r, state.indexes)) return false;
    if (!rowMatchesSector(r, state.sectors)) return false;
    if (!rowMatchesRubric(r, state.rubricBands)) return false;
    if (!rowMatchesCap(r, state.caps)) return false;
    if (!rowMatchesTickerPrefix(r, state.ticker)) return false;
    return true;
  });
}

export function buildFacetOptions({ staticOptions, countsByValue, selectedValues }) {
  const selected = new Set(selectedValues || []);
  const out = [];
  for (const opt of staticOptions || []) {
    const c = countsByValue[opt.value] || 0;
    if (c > 0 || selected.has(opt.value)) {
      const label = opt.label + (c > 0 ? ` (${c})` : " (0)");
      out.push({ value: opt.value, label });
    }
  }
  return out;
}

export function prefetchTickersFromRows(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const t = r && r.underlying_ticker;
    if (t && !seen.has(t)) { seen.add(t); out.push(t); }
  }
  return out;
}

/** @typedef {{ id: string, label: string, sortKey: string|null, sortType: string|null, num?: boolean, hideLocked?: boolean, positionAnchor?: string|null, draggable?: boolean, hint?: string, compactHint?: string, heldTh?: boolean }} ColumnMeta */

/**
 * Header tooltip for a screener column. Compact view stacks a second metric under
 * most cells, so the tip gains the `compactHint` sentence describing that subline.
 */
export function columnHeaderHint(
  meta: { hint?: string; compactHint?: string } | undefined,
  compact: boolean,
): string {
  const base = (meta && meta.hint) || "";
  const sub = compact && meta && meta.compactHint ? meta.compactHint : "";
  if (!base) return sub;
  return sub ? `${base} ${sub}` : base;
}

/** @type {ColumnMeta[]} */
export const PUT_COLUMN_META = [
  { id: "underlying_ticker", label: "Ticker", sortKey: "underlying_ticker", sortType: "str", hideLocked: true, positionAnchor: "first", draggable: false, hint: "Underlying stock symbol with compact index badges (SPX, NDX…) stacked beside the ticker. Click the ticker for analysis + heatmap." },
  { id: "market_cap", label: "Cap", sortKey: "market_cap", sortType: "num", num: true, draggable: true, hint: "Capitalization (billions). Market capitalization via Finviz, shown in billions USD." },
  { id: "sector", label: "Sector", sortKey: "sector", sortType: "str", draggable: true, hint: "Sector classification (via Finviz)." },
  { id: "strike", label: "Strike", sortKey: "strike", sortType: "num", num: true, hideLocked: true, draggable: true, hint: "Strike price — you'd buy 100 shares per contract here if assigned.", compactHint: "Subline: PoP." },
  { id: "underlying_price", label: "Price", sortKey: "underlying_price", sortType: "num", num: true, draggable: true, hint: "Latest underlying share price.", compactHint: "Subline: tgt = analyst price target." },
  { id: "target_price", label: "Target", sortKey: "target_price", sortType: "num", num: true, draggable: true, hint: "Consensus analyst price target (via Finviz) — third-party data, not The Stock Forge's forecast or rating; check the date." },
  { id: "decision", label: "Rubric band", sortKey: "decision", sortType: "str", draggable: true, hint: "Latest stored fundamental report rubric band for this ticker (not a recommendation). Click to open the report when one exists." },
  { id: "expiration_date", label: "Expiry", sortKey: "expiration_date", sortType: "str", hideLocked: true, draggable: true, hint: "Option expiration date, with days to expiration on the subline. Amber underline marks expirations on or after the next Finviz earnings date (informational only)." },
  { id: "next_earnings_date", label: "Earnings", sortKey: "next_earnings_date", sortType: "str", draggable: true, hint: "Next earnings date for the underlying (via Finviz). Subline: days until that date; amber when it lands on or before expiry." },
  { id: "dte", label: "DTE", sortKey: "dte", sortType: "num", num: true, draggable: true, hint: "Days to expiration." },
  { id: "mid", label: "Mid", sortKey: "mid", sortType: "num", num: true, draggable: true, hint: "Mid = bid/ask midpoint; a fill at this price is not assured.", compactHint: "Subline: last traded price." },
  { id: "last_price", label: "Last", sortKey: "last_price", sortType: "num", num: true, draggable: true, hint: "Last traded price of the option." },
  { id: "rec_quantity", label: "Size", sortKey: "rec_quantity", sortType: "num", num: true, draggable: true, hint: "Contracts that fit your check size, computed by the app (check size ÷ collateral per contract). A reference figure only — not a position size we recommend; you decide whether and how much to trade." },
  { id: "collateral", label: "Collat", sortKey: "collateral", sortType: "num", num: true, draggable: true, hint: "Cash secured if every contract shown is assigned (strike × 100 × contracts)." },
  { id: "roi", label: "Ann. ROC", sortKey: "roi", sortType: "num", num: true, draggable: true, hint: "Annualized return on collateral from the midpoint premium and DTE (premium ÷ collateral, annualized) — not a realized return; excludes fees, taxes, slippage and assignment.", compactHint: "Subline: premium per day for one contract (premium × 100 ÷ DTE)." },
  { id: "pop", label: "PoP", sortKey: "pop", sortType: "num", num: true, draggable: true, hint: "PoP (model): a 1−|delta| proxy — not a calibrated probability or a profit guarantee.", compactHint: "Compact view stacks PoP under Strike instead of showing this column." },
  { id: "held", label: "Shares", sortKey: null, sortType: null, num: true, hideLocked: true, draggable: true, heldTh: true, hint: "Long shares you hold on this ticker. Green avail subline = shares not tied to open short call contracts." },
  { id: "_actions", label: "", sortKey: null, sortType: null, hideLocked: true, positionAnchor: "last", draggable: false },
];

/** @type {ColumnMeta[]} */
export const CALL_COLUMN_META = [
  { id: "underlying_ticker", label: "Ticker", sortKey: "underlying_ticker", sortType: "str", hideLocked: true, positionAnchor: "first", draggable: false, hint: "Underlying stock symbol with compact index badges (SPX, NDX…) stacked beside the ticker. Click the ticker for analysis + heatmap." },
  { id: "market_cap", label: "Cap", sortKey: "market_cap", sortType: "num", num: true, draggable: true, hint: "Capitalization (billions). Market capitalization via Finviz, shown in billions USD." },
  { id: "sector", label: "Sector", sortKey: "sector", sortType: "str", draggable: true, hint: "Sector classification (via Finviz)." },
  { id: "holding_avg_cost", label: "Avg cost", sortKey: "holding_avg_cost", sortType: "num", num: true, draggable: true, hint: "Your average purchase price for these shares (total buy cost ÷ shares bought). Covered calls are only screened where the strike is at or above this, so assignment wouldn't sell below your cost basis.", compactHint: "Subline: avail = shares not tied to open short call contracts." },
  { id: "strike", label: "Strike", sortKey: "strike", sortType: "num", num: true, hideLocked: true, draggable: true, hint: "Strike price — your shares would be sold here per contract if assigned. Green above your avg cost, red below it.", compactHint: "Subline: PoP." },
  { id: "underlying_price", label: "Price", sortKey: "underlying_price", sortType: "num", num: true, draggable: true, hint: "Latest underlying share price.", compactHint: "Subline: tgt = analyst price target." },
  { id: "target_price", label: "Target", sortKey: "target_price", sortType: "num", num: true, draggable: true, hint: "Consensus analyst price target (via Finviz) — third-party data, not The Stock Forge's forecast or rating; check the date." },
  { id: "decision", label: "Rubric band", sortKey: "decision", sortType: "str", draggable: true, hint: "Latest stored fundamental report rubric band for this ticker (not a recommendation). Click to open the report when one exists." },
  { id: "expiration_date", label: "Expiry", sortKey: "expiration_date", sortType: "str", hideLocked: true, draggable: true, hint: "Option expiration date, with days to expiration on the subline. Amber underline marks expirations on or after the next Finviz earnings date (informational only)." },
  { id: "dte", label: "DTE", sortKey: "dte", sortType: "num", num: true, draggable: true, hint: "Days to expiration." },
  { id: "mid", label: "Mid", sortKey: "mid", sortType: "num", num: true, draggable: true, hint: "Mid = bid/ask midpoint; a fill at this price is not assured.", compactHint: "Subline: last traded price." },
  { id: "last_price", label: "Last", sortKey: "last_price", sortType: "num", num: true, draggable: true, hint: "Last traded price of the option." },
  { id: "available_call_contracts", label: "Size", sortKey: "available_call_contracts", sortType: "num", num: true, draggable: true, hint: "Covered-call contracts you can sell from your shares (100 shares = 1 contract)." },
  { id: "collateral", label: "Collat", sortKey: "collateral", sortType: "num", num: true, draggable: true, hint: "Share value committed if assigned (strike × 100 × contracts)." },
  { id: "roi", label: "Ann. ROC", sortKey: "roi", sortType: "num", num: true, draggable: true, hint: "Annualized return from the midpoint premium and DTE (premium ÷ committed share value, annualized) — not a realized return; excludes fees, taxes, slippage and assignment.", compactHint: "Compact view stacks this under Profit. instead of showing the column." },
  { id: "pop", label: "PoP", sortKey: "pop", sortType: "num", num: true, draggable: true, hint: "PoP (model): a 1−|delta| proxy — not a calibrated probability or a profit guarantee.", compactHint: "Compact view stacks PoP under Strike instead of showing this column." },
  { id: "open", label: "Shares", sortKey: null, sortType: null, num: true, hideLocked: true, draggable: true, heldTh: true, hint: "Long shares you hold on this ticker. Green avail subline = shares not tied to open short call contracts." },
  { id: "_actions", label: "", sortKey: null, sortType: null, hideLocked: true, positionAnchor: "last", draggable: false },
];

export function columnMetaForSide(side) {
  return side === "call" ? CALL_COLUMN_META : PUT_COLUMN_META;
}

export function defaultColumnOrder(side) {
  return columnMetaForSide(side).map((c) => c.id);
}

export function defaultColumnLayout(side) {
  return { order: defaultColumnOrder(side), hidden: [], preset: "all" };
}

function anchorNormalize(order, defs) {
  const ids = order.slice();
  const first = defs.find((d) => d.positionAnchor === "first");
  const last = defs.find((d) => d.positionAnchor === "last");
  if (first) {
    const i = ids.indexOf(first.id);
    if (i > 0) { ids.splice(i, 1); ids.unshift(first.id); }
  }
  if (last) {
    const i = ids.indexOf(last.id);
    if (i >= 0 && i !== ids.length - 1) { ids.splice(i, 1); ids.push(last.id); }
  }
  return ids;
}

export function normalizeColumnLayout(saved, side) {
  const defs = columnMetaForSide(side);
  const defaultOrder = defs.map((d) => d.id);
  const known = new Set(defaultOrder);
  let order = defaultOrder.slice();
  let hidden = [];
  let preset = "all";
  if (saved && typeof saved === "object") {
    if (Array.isArray(saved.order)) {
      order = saved.order.filter((id) => known.has(id));
      for (const id of defaultOrder) {
        if (!order.includes(id)) order.push(id);
      }
    }
    if (Array.isArray(saved.hidden)) {
      hidden = saved.hidden.filter((id) => {
        const d = defs.find((x) => x.id === id);
        return d && !d.hideLocked;
      });
    }
    if (saved.preset === "custom") preset = saved.preset;
    else if (saved.preset === "core") {
      // Legacy preset — treat as custom with all optional columns hidden.
      preset = "custom";
      hidden = optionalColumnIds(defs);
    }
  }
  order = anchorNormalize(order, defs);
  return { order, hidden, preset };
}

export function optionalColumnIds(defs) {
  return defs.filter((d) => !d.hideLocked).map((d) => d.id);
}

export function getVisibleColumnIds(order, hidden) {
  const hid = new Set(hidden || []);
  return (order || []).filter((id) => !hid.has(id));
}

export function reorderVisibleColumn(order, hidden, fromId, toVisibleIndex, defs) {
  const visible = getVisibleColumnIds(order, hidden);
  const fromVis = visible.indexOf(fromId);
  if (fromVis < 0) return order.slice();
  const toVis = Math.max(0, Math.min(toVisibleIndex, visible.length - 1));
  if (fromVis === toVis) return order.slice();
  const newVisible = visible.slice();
  newVisible.splice(fromVis, 1);
  newVisible.splice(toVis, 0, fromId);
  const hid = new Set(hidden || []);
  const visQueue = newVisible.slice();
  const rebuilt = order.map((id) => (hid.has(id) ? id : visQueue.shift()));
  return defs ? anchorNormalize(rebuilt, defs) : rebuilt;
}

export function moveColumnInOrder(order, hidden, colId, delta, defs) {
  const visible = getVisibleColumnIds(order, hidden);
  const draggable = visible.filter((id) => {
    const d = defs.find((x) => x.id === id);
    return d && d.draggable;
  });
  const idx = draggable.indexOf(colId);
  if (idx < 0) return order.slice();
  const newIdx = idx + delta;
  if (newIdx < 0 || newIdx >= draggable.length) return order.slice();
  const targetVisIdx = visible.indexOf(draggable[newIdx]);
  return reorderVisibleColumn(order, hidden, colId, targetVisIdx, defs);
}

export function getDraggableVisibleColumnIds(order, hidden, defs) {
  return getVisibleColumnIds(order, hidden).filter((id) => {
    const d = defs.find((x) => x.id === id);
    return d && d.draggable;
  });
}
